import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { BrandLoader } from '../components/BrandLoader';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Building2, User, Stethoscope, Mail, Lock, Phone, Globe, MapPin, Briefcase,
  Hash, AlertCircle, CheckCircle, ArrowLeft, ArrowRight, Loader2, Search,
  ShieldCheck, FileSignature, Sparkles, CheckCircle2,
} from 'lucide-react';
import { SpecialtyMultiSelect } from '../components/SpecialtyMultiSelect';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ROLE_OPTIONS = [
  {
    key: 'company',
    title: 'Company',
    desc: 'Register your company / sponsor organization. Once approved, your company will manage its own representatives.',
    icon: Building2,
    accent: 'from-blue-600 to-blue-800',
  },
  // iter151 — public "Company Rep" tile removed. Reps now register only via
  // invitation link generated by the Company Owner from the Manage Reps page.
  {
    key: 'investigator',
    title: 'Investigator',
    desc: 'Register as a Principal Investigator, Co-Investigator, or Consultant with one or more medical specialties.',
    icon: Stethoscope,
    accent: 'from-violet-600 to-violet-800',
  },
];

// iter165g — Register page shell rebuilt to match the new Login UI: glassy
// left form column with brand mark + back-to-home link, and a navy
// showcase right column that matches the landing CardSwap / MagicBento
// palette (deep navy gradient, dotted grid, cyan hairline, glow blobs).
// The right pane uses the dynamic rightTitle / rightSubtitle props passed
// by the parent and renders 3 brand value-prop chips below.
function PageShell({ children, rightTitle, rightSubtitle }) {
  return (
    <div
      className="min-h-screen w-full grid lg:grid-cols-12 bg-slate-50"
      data-testid="register-page"
    >
      {/* ====================== LEFT — Form ====================== */}
      <div className="relative lg:col-span-5 flex items-center justify-center px-6 py-10 lg:py-12 overflow-hidden">
        {/* Soft gradient blobs for depth */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 -left-24 w-[420px] h-[420px] bg-blue-100/60 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-[380px] h-[380px] bg-indigo-100/50 rounded-full blur-3xl" />
        </div>

        <div className="relative w-full max-w-md">
          {/* Back-to-home */}
          <Link
            to="/"
            className="group inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors mb-8"
            data-testid="register-home-link"
          >
            <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
            Back to home
          </Link>

          {/* Brand mark */}
          <div className="flex items-center gap-3 mb-8">
            <img
              src="/favicon-192.png"
              alt="ClinicalFlow logo"
              className="h-12 w-12 object-contain drop-shadow-sm"
            />
            <div className="leading-none">
              <div className="text-2xl font-bold tracking-tight text-slate-900">
                ClinicalFlow
              </div>
              <div className="mt-1.5 text-[11px] uppercase tracking-[0.22em] font-mono text-slate-500">
                Research Request Portal
              </div>
            </div>
          </div>

          {/* Form card — glassy with subtle ring + shadow.
              The inner shadcn Card already provides its own white surface
              and shadow; the halo + ring here lift the whole block off the
              page so it reads as a single hero element. */}
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-br from-blue-200/40 via-indigo-200/30 to-cyan-200/40 rounded-3xl blur-xl pointer-events-none" />
            <div className="relative rounded-2xl bg-white/95 backdrop-blur-xl ring-1 ring-slate-200/80 shadow-2xl shadow-slate-300/40 [&_>div]:!shadow-none [&_>div]:!border-0 [&_>div]:!bg-transparent">
              {children}
            </div>
          </div>

          {/* Footer compliance strip */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
              21 CFR Part 11 ready
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5 text-slate-500" />
              End-to-end encrypted
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-amber-500" />
              Audit-trail backed
            </span>
          </div>
        </div>
      </div>

      {/* ====================== RIGHT — Brand showcase ====================== */}
      <div
        className="relative hidden lg:flex lg:col-span-7 overflow-hidden"
        aria-hidden
      >
        {/* Layered navy gradient that matches landing CardSwap / MagicBento */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(120% 80% at 0% 0%, rgba(56,189,248,0.18) 0%, transparent 55%), radial-gradient(100% 80% at 100% 100%, rgba(99,102,241,0.22) 0%, transparent 60%), linear-gradient(135deg, #0a1f3d 0%, #112b52 50%, #06122a 100%)',
          }}
        />
        {/* Dotted grid for depth */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.07]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.85) 1px, transparent 0)',
            backgroundSize: '22px 22px',
          }}
        />
        {/* Glow accents */}
        <div className="absolute -top-32 -right-32 w-[460px] h-[460px] rounded-full bg-cyan-500/15 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-[460px] h-[460px] rounded-full bg-fuchsia-500/15 blur-3xl" />
        {/* Top cyan hairline */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400 opacity-80" />

        <div className="relative w-full flex flex-col justify-between p-12 xl:p-16 text-white">
          {/* Top row */}
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.07] ring-1 ring-white/15 backdrop-blur text-[11px] uppercase tracking-[0.2em] font-semibold text-cyan-100">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-300 animate-pulse" />
              Join · ClinicalFlow
            </span>
            <span className="text-[11px] font-mono tracking-wider text-blue-100/60">
              clinicalflow.app
            </span>
          </div>

          {/* Middle — dynamic title + subtitle */}
          <div className="max-w-xl animate-[fadeUp_0.55s_ease-out_both]">
            <div className="text-[11px] uppercase tracking-[0.28em] font-mono text-cyan-300/80 mb-5">
              The clinical research OS
            </div>
            <h2 className="text-4xl xl:text-[44px] font-bold tracking-tight leading-[1.08]">
              {rightTitle}
            </h2>
            <p className="mt-5 text-base xl:text-lg text-blue-100/80 leading-relaxed">
              {rightSubtitle}
            </p>
          </div>

          {/* Bottom — value prop pills + KPI bar */}
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              {[
                { icon: FileSignature, label: 'Granular e-signatures', desc: 'Per-document signers, ordered by stage.' },
                { icon: ShieldCheck,   label: 'Immutable audit trail',   desc: 'Every action timestamped & QR-verified.' },
                { icon: Sparkles,      label: 'Role-based dashboards',   desc: 'Each stakeholder sees only what they need.' },
              ].map(({ icon: Icon, label, desc }, i) => (
                <div
                  key={label}
                  className="rounded-2xl bg-white/[0.05] ring-1 ring-white/10 backdrop-blur-xl px-4 py-4 animate-[fadeUp_0.55s_ease-out_both]"
                  style={{ animationDelay: `${100 + i * 110}ms` }}
                >
                  <div className="h-9 w-9 rounded-xl bg-white/[0.07] ring-1 ring-white/15 flex items-center justify-center">
                    <Icon className="h-[18px] w-[18px] text-cyan-200" />
                  </div>
                  <div className="mt-3 text-[13px] font-semibold text-white leading-tight">
                    {label}
                  </div>
                  <div className="mt-1 text-[11px] text-blue-100/70 leading-snug">
                    {desc}
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 backdrop-blur-xl px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div>
                  <div className="text-2xl font-bold bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent">200+</div>
                  <div className="text-[10px] uppercase tracking-wider text-blue-100/70 mt-0.5">Active users</div>
                </div>
                <div className="h-8 w-px bg-white/10" />
                <div>
                  <div className="text-2xl font-bold bg-gradient-to-r from-violet-300 to-fuchsia-400 bg-clip-text text-transparent">30+</div>
                  <div className="text-[10px] uppercase tracking-wider text-blue-100/70 mt-0.5">Specialties</div>
                </div>
                <div className="h-8 w-px bg-white/10" />
                <div>
                  <div className="text-2xl font-bold bg-gradient-to-r from-amber-300 to-orange-400 bg-clip-text text-transparent">15+</div>
                  <div className="text-[10px] uppercase tracking-wider text-blue-100/70 mt-0.5">Sponsors</div>
                </div>
              </div>
              <div className="inline-flex items-center gap-1.5 text-[11px] text-emerald-200 font-semibold">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Trusted by research centers
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Local keyframes for the fade-up entrance */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function ErrorBox({ error }) {
  if (!error) return null;
  return (
    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm animate-slide-down">
      <AlertCircle size={16} />
      <span>{error}</span>
    </div>
  );
}

function SuccessScreen({ message }) {
  return (
    <Card className="border-0 shadow-xl">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-['Manrope']">Registration received</CardTitle>
        <CardDescription>An administrator will review your account shortly.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-center pt-2">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>
        <p className="text-center text-slate-600">{message}</p>
        <p className="text-center text-sm text-slate-500">
          You will receive an email once your account is activated. Please check your inbox (including spam) for updates.
        </p>
        <Link to="/login">
          <Button variant="outline" className="w-full" data-testid="back-to-login-btn">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to login
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

function RoleSelector({ onPick }) {
  return (
    <Card className="border-0 shadow-xl">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-['Manrope']">Create an account</CardTitle>
        <CardDescription>Select the type of account you want to create</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {ROLE_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          return (
            <button
              key={opt.key}
              onClick={() => onPick(opt.key)}
              className="group w-full text-left rounded-xl border border-slate-200 p-4 hover:border-blue-500 hover:shadow-md transition-all flex items-start gap-4"
              data-testid={`role-pick-${opt.key}`}
            >
              <div className={`w-11 h-11 rounded-lg bg-gradient-to-br ${opt.accent} flex items-center justify-center shrink-0 shadow-sm`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900">{opt.title}</h3>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-transform" />
                </div>
                <p className="text-sm text-slate-500 mt-1 leading-relaxed">{opt.desc}</p>
              </div>
            </button>
          );
        })}
        <div className="text-center pt-4 text-sm text-slate-500">
          Already have an account?{' '}
          <Link to="/login" className="text-blue-600 hover:text-blue-800" data-testid="login-link-from-register">
            Sign in
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

function CompanyForm({ onBack, onSubmitted }) {
  const [form, setForm] = useState({
    name: '', contact_email: '', password: '', confirm: '',
    phone: '', website: '', address: '', industry: '', tax_id: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 6) return setError('Password must be at least 6 characters');
    if (form.password !== form.confirm) return setError('Passwords do not match');
    setLoading(true);
    try {
      const payload = { ...form };
      delete payload.confirm;
      if (payload.contact_email) payload.contact_email = payload.contact_email.trim().toLowerCase();
      await axios.post(`${API_URL}/auth/register/company`, payload);
      onSubmitted();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to register');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-0 shadow-xl">
      <CardHeader className="space-y-1">
        <button onClick={onBack} className="flex items-center text-sm text-slate-500 hover:text-slate-700 mb-2" data-testid="back-to-roles-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Choose a different account type
        </button>
        <CardTitle className="text-2xl font-['Manrope']">Register your Company</CardTitle>
        <CardDescription>Once approved, your company will be able to sign in and manage its representatives.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4" data-testid="company-register-form">
          <ErrorBox error={error} />
          <div className="space-y-2">
            <Label htmlFor="name">Company Name *</Label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input id="name" required className="pl-10" value={form.name} onChange={update('name')} data-testid="company-name-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="contact_email">Contact Email * (used to sign in)</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input id="contact_email" type="email" required className="pl-10" value={form.contact_email} onChange={update('contact_email')} data-testid="company-email-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                <PasswordInput id="password" required inputClassName="pl-10" value={form.password} onChange={update('password')} data-testid="company-password-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                <PasswordInput id="confirm" required inputClassName="pl-10" value={form.confirm} onChange={update('confirm')} data-testid="company-confirm-input" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="phone" className="pl-10" value={form.phone} onChange={update('phone')} data-testid="company-phone-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="website" className="pl-10" placeholder="https://..." value={form.website} onChange={update('website')} data-testid="company-website-input" />
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input id="address" className="pl-10" value={form.address} onChange={update('address')} data-testid="company-address-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="industry">Industry</Label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="industry" className="pl-10" value={form.industry} onChange={update('industry')} data-testid="company-industry-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tax_id">Tax ID</Label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="tax_id" className="pl-10" value={form.tax_id} onChange={update('tax_id')} data-testid="company-taxid-input" />
              </div>
            </div>
          </div>

          <Button type="submit" className="w-full bg-blue-900 hover:bg-blue-950" disabled={loading} data-testid="company-submit-btn">
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</> : 'Submit registration'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function CompanyRepForm({ onBack, onSubmitted, lockedCompany, invitationToken }) {
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({
    name: '', email: '', password: '', confirm: '',
    phone: '', company_account_id: lockedCompany?.id || '',
  });
  const [loadingCompanies, setLoadingCompanies] = useState(!lockedCompany);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (lockedCompany) return;
    axios.get(`${API_URL}/auth/companies/active`)
      .then((r) => setCompanies(r.data || []))
      .catch(() => setError('Failed to load companies'))
      .finally(() => setLoadingCompanies(false));
  }, [lockedCompany]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.company_account_id) return setError('Please select a company');
    if (form.password.length < 6) return setError('Password must be at least 6 characters');
    if (form.password !== form.confirm) return setError('Passwords do not match');
    setLoading(true);
    try {
      const payload = { ...form };
      delete payload.confirm;
      if (payload.email) payload.email = payload.email.trim().toLowerCase();
      if (invitationToken) payload.invitation_token = invitationToken;
      await axios.post(`${API_URL}/auth/register/company-rep`, payload);
      onSubmitted();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to register');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-0 shadow-xl">
      <CardHeader className="space-y-1">
        {onBack && (
          <button onClick={onBack} className="flex items-center text-sm text-slate-500 hover:text-slate-700 mb-2" data-testid="back-to-roles-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Choose a different account type
          </button>
        )}
        <CardTitle className="text-2xl font-['Manrope']">Register as Company Rep</CardTitle>
        <CardDescription>
          {lockedCompany
            ? <>You've been invited to join <strong>{lockedCompany.name}</strong>. Complete your profile below.</>
            : 'Choose the company you represent. An administrator will review your registration.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4" data-testid="rep-register-form">
          <ErrorBox error={error} />
          <div className="space-y-2">
            <Label htmlFor="rep_name">Full Name *</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input id="rep_name" required className="pl-10" value={form.name} onChange={update('name')} data-testid="rep-name-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="rep_email">Email *</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input id="rep_email" type="email" required className="pl-10" value={form.email} onChange={update('email')} data-testid="rep-email-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="company">Company *</Label>
            {lockedCompany ? (
              <Input value={lockedCompany.name} disabled data-testid="rep-locked-company" />
            ) : (
              <select
                id="company"
                required
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm bg-white"
                value={form.company_account_id}
                onChange={update('company_account_id')}
                disabled={loadingCompanies}
                data-testid="rep-company-select"
              >
                <option value="">{loadingCompanies ? 'Loading…' : 'Select your company'}</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
            {!lockedCompany && !loadingCompanies && companies.length === 0 && (
              <p className="text-xs text-slate-500">No active companies yet. Ask your company to register first.</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="rep_phone">Phone</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input id="rep_phone" className="pl-10" value={form.phone} onChange={update('phone')} data-testid="rep-phone-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="rep_password">Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                <PasswordInput id="rep_password" required inputClassName="pl-10" value={form.password} onChange={update('password')} data-testid="rep-password-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rep_confirm">Confirm Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                <PasswordInput id="rep_confirm" required inputClassName="pl-10" value={form.confirm} onChange={update('confirm')} data-testid="rep-confirm-input" />
              </div>
            </div>
          </div>

          <Button type="submit" className="w-full bg-blue-900 hover:bg-blue-950" disabled={loading} data-testid="rep-submit-btn">
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</> : 'Submit registration'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function InvestigatorForm({ onBack, onSubmitted, lockedEmail, lockedSpecialties, consultantInvitationToken }) {
  const [form, setForm] = useState({
    name: '', email: lockedEmail || '', password: '', confirm: '', phone: '', bio: '',
  });
  const [specialties, setSpecialties] = useState([]);
  const [selectedSpecs, setSelectedSpecs] = useState(new Set((lockedSpecialties || []).map(s => s.id)));
  const [regSpecSearch, setRegSpecSearch] = useState('');
  const [loadingSpecs, setLoadingSpecs] = useState(!lockedSpecialties);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (lockedSpecialties && lockedSpecialties.length) {
      setSpecialties(lockedSpecialties);
      setLoadingSpecs(false);
      return;
    }
    axios.get(`${API_URL}/specialties`)
      .then((r) => setSpecialties(r.data || []))
      .catch(() => setError('Failed to load specialties'))
      .finally(() => setLoadingSpecs(false));
  }, [lockedSpecialties]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleSpec = (id) => {
    if (lockedSpecialties && lockedSpecialties.find(s => s.id === id)) return; // locked specialty cannot be unselected
    const next = new Set(selectedSpecs);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedSpecs(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (selectedSpecs.size === 0) return setError('Please select at least one specialty');
    if (form.password.length < 6) return setError('Password must be at least 6 characters');
    if (form.password !== form.confirm) return setError('Passwords do not match');
    setLoading(true);
    try {
      const payload = {
        name: form.name, email: (form.email || '').trim().toLowerCase(), password: form.password,
        phone: form.phone, bio: form.bio, specialty_ids: Array.from(selectedSpecs),
      };
      if (consultantInvitationToken) payload.consultant_invitation_token = consultantInvitationToken;
      await axios.post(`${API_URL}/auth/register/investigator`, payload);
      onSubmitted();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to register');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-0 shadow-xl">
      <CardHeader className="space-y-1">
        {onBack && (
          <button onClick={onBack} className="flex items-center text-sm text-slate-500 hover:text-slate-700 mb-2" data-testid="back-to-roles-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Choose a different account type
          </button>
        )}
        <CardTitle className="text-2xl font-['Manrope']">Register as Investigator</CardTitle>
        <CardDescription>{lockedEmail ? 'Complete your profile to start working on the feasibility request you were invited to.' : 'Pick your specialties and tell us a little about yourself. An administrator will activate your account shortly.'}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4" data-testid="investigator-register-form">
          <ErrorBox error={error} />
          <div className="space-y-2">
            <Label htmlFor="inv_name">Full Name *</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input id="inv_name" required className="pl-10" value={form.name} onChange={update('name')} data-testid="investigator-name-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="inv_email">Email *</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input id="inv_email" type="email" required className="pl-10" value={form.email} onChange={update('email')} disabled={!!lockedEmail} data-testid="investigator-email-input" />
            </div>
            {lockedEmail && (
              <p className="text-xs text-slate-500">This invitation is locked to <strong>{lockedEmail}</strong>.</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="inv_phone">Phone</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input id="inv_phone" className="pl-10" value={form.phone} onChange={update('phone')} data-testid="investigator-phone-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Specialties * (select one or more)</Label>
            <SpecialtyMultiSelect
              specialties={specialties}
              value={Array.from(selectedSpecs)}
              onChange={(ids) => setSelectedSpecs(new Set(ids))}
              testidPrefix="investigator-specialty"
              disabledIds={(lockedSpecialties || []).map((s) => s.id)}
              emptyText={loadingSpecs ? 'Loading…' : 'No specialties available'}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="inv_bio">Short bio / credentials</Label>
            <Textarea id="inv_bio" rows={3} placeholder="e.g. Cardiologist with 10 years of clinical research experience..." value={form.bio} onChange={update('bio')} data-testid="investigator-bio-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="inv_password">Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                <PasswordInput id="inv_password" required inputClassName="pl-10" value={form.password} onChange={update('password')} data-testid="investigator-password-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="inv_confirm">Confirm Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                <PasswordInput id="inv_confirm" required inputClassName="pl-10" value={form.confirm} onChange={update('confirm')} data-testid="investigator-confirm-input" />
              </div>
            </div>
          </div>

          <Button type="submit" className="w-full bg-blue-900 hover:bg-blue-950" disabled={loading} data-testid="investigator-submit-btn">
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</> : 'Submit registration'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export default function Register() {
  const [params] = useSearchParams();
  const initialRole = params.get('as');
  const [role, setRole] = useState(['company', 'company-rep', 'investigator'].includes(initialRole) ? initialRole : null);
  const [submitted, setSubmitted] = useState(false);

  const right = useMemo(() => {
    if (submitted) return { title: 'Almost there!', subtitle: "We've received your registration. Check your inbox for updates." };
    if (role === 'company') return { title: 'Bring your company aboard.', subtitle: 'Run feasibility studies, clinical trial agreements, and approvals — all on one auditable platform.' };
    if (role === 'company-rep') return { title: 'Represent your company.', subtitle: 'Submit, sign, and track requests on behalf of your company.' };
    if (role === 'investigator') return { title: 'Join the research center.', subtitle: 'Get matched to feasibility and review work that fits your specialties.' };
    return { title: 'Create your ClinicalFlow account.', subtitle: 'A unified portal for sponsors, investigators, reviewers, and administrators.' };
  }, [role, submitted]);

  if (submitted) {
    return (
      <PageShell rightTitle={right.title} rightSubtitle={right.subtitle}>
        <SuccessScreen message="Your registration has been submitted. An administrator will review and activate your account." />
      </PageShell>
    );
  }

  return (
    <PageShell rightTitle={right.title} rightSubtitle={right.subtitle}>
      {!role && <RoleSelector onPick={setRole} />}
      {role === 'company' && <CompanyForm onBack={() => setRole(null)} onSubmitted={() => { toast.success('Registration submitted'); setSubmitted(true); }} />}
      {role === 'company-rep' && <CompanyRepForm onBack={() => setRole(null)} onSubmitted={() => { toast.success('Registration submitted'); setSubmitted(true); }} />}
      {role === 'investigator' && <InvestigatorForm onBack={() => setRole(null)} onSubmitted={() => { toast.success('Registration submitted'); setSubmitted(true); }} />}
    </PageShell>
  );
}

export function RegisterByInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: '', company: null });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/auth/invitation/${token}`)
      .then((r) => setState({ loading: false, error: '', company: r.data.company }))
      .catch((err) => setState({ loading: false, error: err.response?.data?.detail || 'Invalid invitation', company: null }));
  }, [token]);

  if (submitted) {
    return (
      <PageShell rightTitle="Almost there!" rightSubtitle="We've received your registration. Check your inbox for updates.">
        <SuccessScreen message="Your registration has been submitted. An administrator will review and activate your account." />
      </PageShell>
    );
  }

  if (state.loading) {
    return (
      <PageShell rightTitle="Loading invitation…" rightSubtitle="Please wait while we validate your invite link.">
        <Card className="border-0 shadow-xl">
          <CardContent className="py-12 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  if (state.error || !state.company) {
    return (
      <PageShell rightTitle="Invitation issue" rightSubtitle="There was a problem with this invitation link.">
        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle>Invitation not valid</CardTitle>
            <CardDescription>{state.error || 'This invitation link is no longer valid.'}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/login')} className="w-full" data-testid="invite-back-login">Back to sign in</Button>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  return (
    <PageShell
      rightTitle={`Welcome to ${state.company.name}`}
      rightSubtitle="Complete your profile below to start representing your company on ClinicalFlow."
    >
      <CompanyRepForm
        onBack={null}
        onSubmitted={() => { toast.success('Registration submitted'); setSubmitted(true); }}
        lockedCompany={state.company}
        invitationToken={token}
      />
    </PageShell>
  );
}


export function RegisterByConsultantInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: '', email: null, specialties: [], request: null });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/auth/consultant-invitation/${token}`)
      .then((r) => setState({
        loading: false, error: '',
        email: r.data.email,
        specialties: r.data.specialties || [],
        request: r.data.request || null,
      }))
      .catch((err) => setState({ loading: false, error: err.response?.data?.detail || 'Invalid invitation', email: null, specialties: [], request: null }));
  }, [token]);

  if (submitted) {
    return (
      <PageShell rightTitle="Almost there!" rightSubtitle="We've received your registration. Once an administrator approves your account you'll be auto-routed to the feasibility request.">
        <SuccessScreen message="Your registration has been submitted. An administrator will review and activate your account, then you'll be automatically added to the feasibility request that invited you." />
      </PageShell>
    );
  }

  if (state.loading) {
    return (
      <PageShell rightTitle="Loading invitation…" rightSubtitle="Please wait while we validate your invite link.">
        <Card className="border-0 shadow-xl">
          <CardContent className="py-12 flex items-center justify-center">
            <BrandLoader size="md" label="Validating invitation…" />
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  if (state.error || !state.email) {
    return (
      <PageShell rightTitle="Invitation issue" rightSubtitle="There was a problem with this invitation link.">
        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle>Invitation not valid</CardTitle>
            <CardDescription>{state.error || 'This invitation link is no longer valid.'}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/login')} className="w-full" data-testid="invite-back-login">Back to sign in</Button>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  const refLine = state.request?.ref_no ? `${state.request.ref_no}${state.request.title ? ` — ${state.request.title}` : ''}` : 'a feasibility request';

  return (
    <PageShell
      rightTitle="You're invited as an Investigator"
      rightSubtitle={`Once approved, you'll auto-route to ${refLine}.`}
    >
      <InvestigatorForm
        onBack={null}
        lockedEmail={state.email}
        lockedSpecialties={state.specialties}
        consultantInvitationToken={token}
        onSubmitted={() => { toast.success('Registration submitted'); setSubmitted(true); }}
      />
    </PageShell>
  );
}
